// 인증 토큰 + fetch 래퍼 — 바닐라 app.js/dcf.js 에 2벌 복붙돼 있던 것을 1곳으로 통합.
// 토큰 저장 키·헤더 이름은 기존 프론트와 동일해야 한다(같은 도메인에서 로그인 공유).

const TOKEN_KEY = "token";

export function getToken(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function setToken(t: string) {
  try {
    localStorage.setItem(TOKEN_KEY, t);
  } catch {
    /* private mode 등 */
  }
}

export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* noop */
  }
}

export function authHeaders(): Record<string, string> {
  const t = getToken();
  return t ? { "X-DeepSearch-User-Token": t } : {};
}

/** JSON API 호출 — 401 이면 AuthError 를 던져 로그인 오버레이로 유도한다. */
export class AuthError extends Error {}

/** 게이트웨이가 돌려준 오류의 사람용 문구.
 *
 *  본문 없는 502/503/504 는 앱이 아니라 인그레스가 낸 것이다 — 이 서비스들은
 *  FastAPI 라 오류도 {ok:false, message} 로 준다. valuation-2 는 인메모리 상태
 *  때문에 파드 1개로 고정돼 있어, 재시작 동안 정상 파드가 없어 이 상태가 잠깐 뜬다.
 *  사용자에게 'HTTP 503' 을 보여주면 무엇을 해야 할지 알 수 없다. */
export function gatewayMessage(status: number): string {
  if (status === 502 || status === 503)
    return "서버가 잠시 재시작 중입니다. 30초쯤 뒤에 다시 시도해 주세요.";
  if (status === 504)
    return "서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해 주세요.";
  if (status === 429)
    return "요청이 몰려 잠시 제한되었습니다. 잠시 후 다시 시도해 주세요.";
  if (status >= 500)
    return "서버에서 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.";
  return "";
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  // 401 은 두 번 재시도 후에만 인증 실패로 취급한다 — 로그인 직후 발급된
  // 토큰은 서버 검증 캐시/전파 지연으로 첫 요청이 일시적으로 401 이 날 수
  // 있다 (바닐라에서 E2E 로 확인된 프로덕션 이슈와 동일 원인).
  for (let attempt = 0; ; attempt++) {
    const r = await fetch(path, {
      ...init,
      headers: {
        ...(init.body && !(init.body instanceof FormData)
          ? { "Content-Type": "application/json" }
          : {}),
        ...authHeaders(),
        ...(init.headers || {}),
      },
    });
    const d = await r.json().catch(() => ({}));
    if (r.status === 401) {
      if (path !== "/agent/login" && attempt < 2 && getToken()) {
        await new Promise((res) => setTimeout(res, 1200 * (attempt + 1)));
        continue;
      }
      throw new AuthError((d as { message?: string }).message || "로그인이 필요합니다.");
    }
    if (r.status === 403 && (d as { code?: string }).code === "ENTERPRISE_REQUIRED") {
      throw new AuthError((d as { message?: string }).message || "엔터프라이즈 계정만 이용할 수 있습니다.");
    }
    if (!r.ok) {
      const msg = (d as { message?: string }).message;
      throw new Error(msg || gatewayMessage(r.status) || `HTTP ${r.status}`);
    }
    return d as T;
  }
}
